import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats } from '@/mocks/homeData';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeyValue = (formData.get('company_alt') as string || '').trim();
    if (honeyValue) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    setFormError('');
    try {
      const res = await fetch('http://yashisz.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫漫画网站',
    description: '韩漫漫画网站品牌介绍、用户口碑和联系方式',
    url: 'http://yashisz.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫漫画网站',
      url: 'http://yashisz.com/',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫漫画网站',
    url: 'http://yashisz.com/',
    description: '专注于韩国漫画大全、推荐和排行榜的中文阅读平台',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@yashisz.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              关于我们
            </h1>
            <p className="text-foreground-500 text-sm mt-3 max-w-2xl">
              了解韩漫漫画网站的品牌故事、核心价值和联系方式
            </p>
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-accent-500 text-xs font-medium tracking-wider uppercase">
                  品牌故事
                </span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mt-3 mb-6">
                  为韩漫爱好者
                  <br />
                  而生
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫漫画网站创立于2020年，是一群热爱韩国漫画的年轻人共同打造的中文韩漫阅读平台。我们深知韩漫爱好者的需求：<strong>全、快、清、免费</strong>——这正是我们始终坚守的四大原则。
                  </p>
                  <p>
                    平台收录了超过 <strong className="text-foreground-950">{stats.totalComics}</strong> 部韩国漫画作品，涵盖恋爱、奇幻、动作、悬疑、科幻、日常等数十个题材分类。无论你喜欢什么类型的韩漫，都能在这里找到心仪的作品。
                  </p>
                  <p>
                    我们坚持<strong>免费阅读</strong>原则，所有漫画内容均向用户免费开放。同时采用独创的<strong>下拉式阅读</strong>技术，让读者可以像浏览网页一样自然流畅地阅读漫画，无需翻页、无需等待加载。
                  </p>
                  <p>
                    每天有超过 <strong className="text-foreground-950">{stats.dailyReaders}</strong> 读者在我们的平台上享受韩漫阅读的乐趣，累计阅读量已突破 <strong className="text-foreground-950">{stats.totalViews}</strong> 次。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20illustration%2C%20cozy%20reading%20scene%20with%20warm%20lighting%2C%20comic%20books%20and%20digital%20tablet%2C%20artistic%20composition%2C%20soft%20cream%20and%20warm%20beige%20tones%2C%20editorial%20quality%2C%20no%20text%2C%20relaxed%20atmosphere&width=800&height=600&seq=hanman-about-story&orientation=landscape"
                  alt="韩漫漫画网站品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-foreground-400 text-xs font-medium tracking-wider uppercase">
                核心价值
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mt-3">
                我们的承诺
              </h2>
              <p className="text-foreground-500 text-sm mt-3 max-w-xl mx-auto">
                以读者为中心，打造最好的韩漫阅读体验
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-book-2-line', title: '海量资源', desc: `收录${stats.totalComics}部韩漫作品，持续更新扩充中` },
                { icon: 'ri-eye-line', title: '高清画质', desc: '所有漫画均支持高清阅览，画面细腻清晰' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '与韩国同步更新，第一时间获取最新章节' },
                { icon: 'ri-vip-crown-line', title: '完全免费', desc: '坚持免费原则，无任何收费项目，无广告干扰' },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 rounded-2xl bg-background-50">
                  <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-accent-500 text-xs font-medium tracking-wider uppercase">
                读者口碑
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mt-3">
                他们怎么说
              </h2>
              <p className="text-foreground-500 text-sm mt-3">
                真实的读者反馈，是我们前进的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { quote: '自从发现韩漫漫画网站，我再也不用到处找资源了。更新速度很快，画质也清晰，每天必刷！', name: '小雨', role: '忠实读者 · 3年' },
                { quote: '这里真的是韩漫爱好者的天堂！分类详细，搜索方便，而且完全免费。强烈推荐给所有喜欢韩漫的朋友。', name: '星辰大海', role: '漫画爱好者 · 2年' },
                { quote: '下拉式阅读体验太棒了，是我用过最好用的韩漫阅读平台。页面设计也很简洁好看，没有乱七八糟的广告。', name: '樱花飘落', role: 'VIP读者 · 1年' },
              ].map((item, i) => (
                <div key={i} className="p-6 rounded-2xl bg-background-100">
                  <i className="ri-double-quotes-l text-3xl text-accent-300"></i>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3 mb-6">{item.quote}</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-accent-600 font-medium text-sm">{item.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-foreground-400 text-xs font-medium tracking-wider uppercase">
                联系我们
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mt-3">
                与我们取得联系
              </h2>
              <p className="text-foreground-500 text-sm mt-3">
                有任何问题或建议，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', content: 'hello@yashisz.com' },
                { icon: 'ri-wechat-line', title: '微信公众号', content: '韩漫漫画' },
                { icon: 'ri-weibo-line', title: '官方微博', content: '@韩漫漫画' },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 rounded-2xl bg-background-50">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="text-sm font-medium text-foreground-950 mb-1">{item.title}</h3>
                  <p className="text-xs text-foreground-500">{item.content}</p>
                </div>
              ))}
            </div>

            <div className="bg-background-50 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading font-semibold text-foreground-950 mb-6 text-center">
                发送消息
              </h3>
              <form
               
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="honeypot-field">
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors resize-none"
                    placeholder="请输入您的消息（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="inline-flex items-center px-10 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>
                </div>

                {formStatus === 'success' && (
                  <p className="text-center text-sm text-green-600">消息发送成功！我们会尽快回复您。</p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="text-center text-sm text-red-500">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}